import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Seo from '../components/Seo';
import EnquiryContacts from '../components/EnquiryContacts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from 'lucide-react';
import { BRANCHES, CONTACT, SERVICES } from '../mock';
import { ENQUIRY_TEAMS, getEnquiryTeam, getEnquiryRequest, getEnquiryMailto } from '../lib/enquiries';

const emptyForm = (service = '', team = '') => ({ name: '', email: '', phone: '', company: '', location: '', service, team, subject: '', message: '' });

export default function Contact() {
  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get('service') || '';
  const selectedService = SERVICES.some(s => s.title === requestedService) ? requestedService : '';
  const requestedTeam = searchParams.get('team') || '';
  const selectedTeam = ENQUIRY_TEAMS.some(team => team.id === requestedTeam) ? requestedTeam : '';
  const [form, setForm] = useState(() => emptyForm(selectedService, selectedTeam));
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  const formRef = useRef(null);
  const teamRef = useRef(null);
  const sending = useRef(false);
  useEffect(() => {
    setForm(current => ({ ...current, service: selectedService }));
  }, [selectedService]);
  useEffect(() => {
    setForm(current => ({ ...current, team: selectedTeam }));
  }, [selectedTeam]);
  const [loading, setLoading] = useState(false);
  const enquiryTeam = getEnquiryTeam(form.team);

  const update = (k) => (e) => {
    setForm(current => ({ ...current, [k]: e.target.value }));
    setStatus('');
    setFailed(false);
  };
  const chooseTeam = (team) => {
    if (sending.current) return;
    setForm(current => ({ ...current, team }));
    setStatus('');
    setFailed(false);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    formRef.current?.scrollIntoView?.({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    teamRef.current?.focus({ preventScroll: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending.current) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: 'Missing fields', description: 'Name, email and message are required.' });
      return;
    }
    sending.current = true;
    setLoading(true);
    setFailed(false);
    setStatus('Sending your enquiry…');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const request = getEnquiryRequest(form);
      const res = await fetch(request.endpoint, {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(request.payload)
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      if (String(data.success) === 'true') {
        toast({ title: 'Message sent', description: 'Thank you — our team will get back to you shortly.' });
        setStatus(`Thank you. Your enquiry to ${enquiryTeam.name} has been submitted, with both admin teams copied.`);
        setForm(emptyForm(form.service, form.team));
      } else {
        throw new Error('Submission not accepted');
      }

    } catch (err) {
      setFailed(true);
      setStatus('Your enquiry could not be confirmed. Your details are still here — please retry or use the email option below.');
      toast({ title: 'Enquiry not confirmed', description: 'Your message is saved in this form. You can also open it in your email app.' });
    } finally {
      clearTimeout(timeout);
      sending.current = false;
      setLoading(false);
    }
  };

  return (
    <div>
      <Seo title="Contact Us | Fixed Group — Cold Chain Specialists Malaysia" description="Contact Fixed Group for reefer container repair, cold-chain haulage and cold room solutions. Call 012-913 0786 / 019-373 5298 or message us — Klang HQ & Kota Kinabalu branch." />
      <PageHeader title="Contact Us" crumbs={[{ label: 'Contact Us' }]} />
      <EnquiryContacts selectedTeam={form.team} onSelectTeam={chooseTeam} disabled={loading} />

      <section className="py-10 md:py-16">
        <div className="fx-container grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-6 order-2 lg:order-1">
            <div>
              <span className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">We’re here to help</span>
              <h2 className="section-title text-3xl md:text-4xl font-extrabold mt-2">Talk to our team</h2>
              <p className="mt-4 text-slate-600 leading-relaxed">Have a reefer, cold room or cold-chain haulage requirement? Send us a message and our team will respond within one business day.</p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3"><Phone className="text-emerald-600 mt-1" size={18} /><div><div className="text-xs uppercase tracking-widest text-slate-500">Phone</div><div className="space-y-1">{CONTACT.phones.map((p) => (<a key={p.tel} href={`tel:${p.tel}`} data-testid={`contact-phone-${p.tel}`} className="block font-medium text-slate-800 hover:text-emerald-700">{p.display}</a>))}</div></div></div>
              <div className="flex items-start gap-3"><Clock className="text-emerald-600 mt-1" size={18} /><div><div className="text-xs uppercase tracking-widest text-slate-500">General enquiry hours</div><div className="font-medium text-slate-800">{CONTACT.hours}</div></div></div>
            </div>

            <aside className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <h3 className="font-bold text-emerald-950">24/7 emergency assistance</h3>
              <p className="mt-2 text-sm text-emerald-900">For urgent reefer or cold room faults, call our support team directly. This form is for general enquiries.</p>
              {CONTACT.phones.slice(1).map(p => <a key={p.tel} href={`tel:${p.tel}`} className="mt-3 block font-semibold text-emerald-800 underline">{p.display}</a>)}
            </aside>
            <div className="grid gap-4">
              {BRANCHES.map((b) => (
                <div key={b.id} className="fx-card overflow-hidden" data-testid={`branch-card-${b.id}`}>
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-emerald-700 font-semibold"><MapPin size={16} /> {b.name}</div>
                    <p className="mt-2 text-sm text-slate-600">{b.address}</p>
                    <a href={b.map} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-sm font-semibold text-emerald-700">Get directions (opens new tab)</a>
                  </div>
                  <iframe
                    title={`Map — ${b.name}`}
                    data-testid={`branch-map-${b.id}`}
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(b.address)}&z=15&output=embed`}
                    className="w-full h-56 border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 min-w-0 order-1 lg:order-2">
            <form ref={formRef} id="enquiry-form" onSubmit={submit} aria-busy={loading} className="fx-card p-5 sm:p-8 scroll-mt-36" data-testid="contact-form">
              <h2 className="text-2xl font-bold">Request a quote</h2>
              <p className="mt-2 mb-6 text-sm text-slate-600">Tell us what you need. Fields marked * are required.</p>
              <fieldset disabled={loading} className="min-w-0">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2 min-w-0">
                  <Label htmlFor="team">Which company would you like to contact?</Label>
                  <select ref={teamRef} id="team" name="team" value={form.team} onChange={update('team')} aria-describedby="team-description" className="mt-2 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 py-3 text-sm">
                    <option value="">General enquiry / Not sure yet</option>
                    {ENQUIRY_TEAMS.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                  </select>
                  <p id="team-description" className="mt-2 text-xs leading-relaxed text-slate-500">{form.team ? `Sent to ${enquiryTeam.name}, with both admin teams copied.` : 'Our logistics team handles general enquiries and will direct your request. Both admin teams receive a copy.'}</p>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="service">Service required</Label>
                  <select id="service" name="service" value={form.service} onChange={update('service')} className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-sm">
                    <option value="">Select a service / Not sure yet</option>
                    {SERVICES.map(s => <option key={s.title} value={s.title}>{s.title}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="company">Company name</Label>
                  <Input id="company" autoComplete="organization" value={form.company} onChange={update('company')} maxLength={200} className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="location">Project location</Label>
                  <Input id="location" value={form.location} onChange={update('location')} maxLength={200} placeholder="e.g. Port Klang, Selangor" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input required autoComplete="name" maxLength={150} id="name" data-testid="contact-name-input" value={form.name} onChange={update('name')} placeholder="Your name" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input required autoComplete="email" maxLength={254} id="email" data-testid="contact-email-input" type="email" value={form.email} onChange={update('email')} placeholder="you@company.com" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input type="tel" autoComplete="tel" maxLength={40} id="phone" value={form.phone} onChange={update('phone')} placeholder="+60 12-345 6789" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input maxLength={200} id="subject" value={form.subject} onChange={update('subject')} placeholder="How can we help?" className="mt-2" />
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="message">Message *</Label>
                <Textarea required maxLength={5000} id="message" data-testid="contact-message-input" value={form.message} onChange={update('message')} rows={6} placeholder="Tell us about your requirement…" className="mt-2" />
              </div>
              <Button type="submit" data-testid="contact-submit-btn" disabled={loading} className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-6 py-6 text-base">
                {loading ? <><Loader2 className="mr-2 animate-spin" size={16} /> Sending...</> : <><Send className="mr-2" size={16} /> Send Message</>}
              </Button>
              </fieldset>
              <p role="status" aria-live="polite" className="mt-4 text-sm font-medium text-slate-700">{status}</p>
              {!loading && <div className={`mt-5 rounded-xl p-4 ${failed ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-200'}`}>
                <a href={getEnquiryMailto(form.team, form)} data-testid="email-fallback" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 underline underline-offset-4"><Mail size={16} aria-hidden="true" /> {failed ? 'Send this enquiry from your email app' : 'Prefer to use your email app?'}</a>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">Opens a draft to {enquiryTeam.email} with both admins copied. Review it and press Send in your email app.</p>
              </div>}
              <p className="mt-3 text-xs text-slate-500">By submitting this form, you agree to be contacted by the Fixed Group team.</p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
